import React from 'react';
import ReactDOM from 'react-dom';

/**
 * The "…" menu carrying Edit and Delete for one activity.
 *
 * WHY THE ACTIONS MOVED INTO A MENU
 * ---------------------------------
 * Clicking an activity now NAVIGATES — a chip on the Schedule board opens its
 * case, a row in the Activities list opens the Schedule page. Edit and Delete
 * on that same surface is how people delete what they meant to open, so the
 * destructive pair sits behind a deliberate second click.
 *
 * WHY IT MIRRORS SlotActionsMenu RATHER THAN scale-portal
 * -------------------------------------------------------
 * This board already has a three-dots menu on availability slots
 * (`SlotActionsMenu` in pages/Schedule.jsx) and an activity chip sits in the
 * same day cell beside it. Matching THAT — same `w-5 h-5` trigger, same
 * `w-52` white panel, same `px-3 py-2 text-xs` rows, same `z-[9999]` portal —
 * is what keeps one menu idiom on the page. The scale-portal twin is built from
 * inline styles because that app's Tailwind build is v2 and has no arbitrary
 * values; copying its styles here would import a constraint this app does not
 * have, and the two would drift the first time either was touched. What must
 * match across the portals is the BEHAVIOUR and the wording, which it does.
 *
 * The portal is not optional: the day cell's `overflow:hidden` clips an in-flow
 * panel, which is the same reason SlotActionsMenu renders through one.
 */

const ThreeDotsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path fillRule="evenodd" d="M4.5 12a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zm6 0a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zm6 0a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0z" clipRule="evenodd" />
  </svg>
);

const GUTTER = 8;

export default function ActivityActionsMenu({ onEdit, onDelete, onOpenChange, inline = false, label = 'Activity actions' }) {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState({ top: 0, left: 0 });
  // Hidden until measured, so the flip/clamp does not flash at the raw click
  // point first — the same guard SlotActionsMenu uses.
  const [ready, setReady] = React.useState(false);
  const btnRef = React.useRef(null);
  const panelRef = React.useRef(null);

  const closeMenu = () => {
    setOpen(false);
    setReady(false);
  };

  /* The chip hides its hover card while this is open — a card and a menu
   * stacked on the same chip fight for the same space, and the card is wide
   * enough to cover the menu's first row. */
  React.useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) {
        closeMenu();
      }
    };
    const onEsc = (e) => { if (e.key === 'Escape') closeMenu(); };
    // A fixed panel does not travel with a scrolling ancestor, so it would end
    // up over the wrong row. `true` catches the scroll on whichever inner
    // container actually moves — the dialog body as well as the window.
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    window.addEventListener('scroll', closeMenu, true);
    window.addEventListener('resize', closeMenu);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
      window.removeEventListener('scroll', closeMenu, true);
      window.removeEventListener('resize', closeMenu);
    };
  }, [open]);

  // Open downward, flip above when the viewport has no room below (an activity
  // in the last calendar row), then clamp fully on-screen.
  React.useLayoutEffect(() => {
    if (!open || !btnRef.current || !panelRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const { height, width } = panelRef.current.getBoundingClientRect();
    const below = r.bottom + 6;
    const flip = below + height > window.innerHeight - GUTTER && r.top - height - 6 > GUTTER;
    const top = flip ? r.top - height - 6 : below;
    const left = Math.max(
      GUTTER,
      Math.min(r.right - width, window.innerWidth - width - GUTTER)
    );
    setPos({ top, left });
    setReady(true);
  }, [open]);

  const toggleOpen = (e) => {
    // The chip body and the day cell beneath it both act on a click; neither
    // may fire when the target is this trigger.
    e.preventDefault();
    e.stopPropagation();
    setOpen((o) => !o);
  };

  const run = (fn) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    closeMenu();
    fn?.();
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggleOpen}
        style={inline ? { width: 24, height: 24, color: '#46534F' } : undefined}
        aria-expanded={open}
        title={label}
        aria-label={label}
        className="flex items-center justify-center w-5 h-5 flex-none rounded text-gray-500 hover:bg-gray-200/60 transition-colors"
      >
        {inline ? <svg width="18" height="18" viewBox="0 0 24 24" fill="#46534F" aria-hidden="true"><circle cx="5" cy="12" r="1.9"/><circle cx="12" cy="12" r="1.9"/><circle cx="19" cy="12" r="1.9"/></svg> : <ThreeDotsIcon />}
      </button>

      {open && ReactDOM.createPortal(
        <div
          ref={panelRef}
          // Above the hover card (10040), which is wide enough to cover this
          // menu's first row and used to swallow "Edit". z-[9999] matched
          // SlotActionsMenu but lost to the card; the z is set inline so it
          // cannot be defeated by a class that fails to generate.
          className="fixed bg-white rounded-lg shadow-lg border border-gray-200 w-40"
          style={{ top: pos.top, left: pos.left, zIndex: 10050, visibility: ready ? 'visible' : 'hidden' }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={run(onEdit)}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-gray-50 border-b border-gray-100"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
            </svg>
            <span className="font-semibold text-gray-800">Edit</span>
          </button>
          <button
            type="button"
            onClick={run(onDelete)}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-red-50"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
            </svg>
            <span className="font-semibold text-red-600">Delete</span>
          </button>
        </div>,
        document.body
      )}
    </>
  );
}
